import * as Y from 'yjs';

import { isYValue } from './isYValue';

export type JSONToY<T> = T extends Array<infer Item>
    ? Y.Array<JSONToY<Item>>
    : T extends { [key: string]: infer V }
      ? Y.Map<JSONToY<V>>
      : T extends string | boolean | null | undefined | number
        ? T
        : Y.AbstractType<any>;

/**
 * Convert any JSON value to a deep Y.js value.
 */
export function jsonToY<Value>(json: Value): JSONToY<Value> {
    if (isYValue(json)) {
        // @ts-ignore
        return json;
    }

    if (isJSONPrimitive(json)) {
        // @ts-ignore
        return json;
    }

    if (Array.isArray(json)) {
        // @ts-ignore
        return arrayToYArray(json);
    }

    if (typeof json === 'object') {
        // @ts-ignore
        return objectToYMap(json);
    }

    throw new Error(`Unknown JSON type for jsonToY: ${typeof json}`);
}

/**
 * Update an existing Yjs value with a JSON value.
 * It returns a new value if the value should be replaced.
 */
export function updateYWithJSON(
    yObject: any,
    json: { [key: string]: any } | Array<any>
): Y.AbstractType<any> | null {
    if (Array.isArray(json)) {
        if (yObject instanceof Y.Array) {
            updateYArrayWithJSON(yObject, json);
            return null;
        }
        return arrayToYArray(json);
    }

    if (typeof json === 'object') {
        if (yObject instanceof Y.Map) {
            updateYMapWithJSON(yObject, json);
            return null;
        }
        return objectToYMap(json);
    }

    throw new Error(`Unknown JSON type for updateYWithJSON: ${typeof json}`);
}

function objectToYMap<Value>(json: { [key: string]: Value }): Y.Map<JSONToY<Value>> {
    const yMap = new Y.Map<JSONToY<Value>>();

    Object.keys(json).forEach((key) => {
        const value = json[key];

        if (isYValue(value) || isJSONPrimitive(value)) {
            // @ts-ignore
            yMap.set(key, value);
        } else {
            yMap.set(key, jsonToY(value));
        }
    });

    return yMap;
}

function updateYMapWithJSON<Value>(yMap: Y.Map<JSONToY<Value>>, json: { [key: string]: Value }) {
    // Delete properties that no longer exists
    for (const key of yMap.keys()) {
        if (!(key in json)) {
            yMap.delete(key);
        }
    }

    // Insert / update properties
    Object.keys(json).forEach((key) => {
        updateYMapKeyWithJSON(yMap, json, key);
    });
}

function updateYMapKeyWithJSON(yMap: Y.Map<any>, json: any, key: string) {
    const value = json[key];
    const current = yMap.get(key);

    if (isYValue(value)) {
        if (value === current) {
            // Skip because of https://github.com/yjs/yjs/issues/551
        } else {
            // @ts-ignore
            yMap.set(key, value);
        }
    } else if (isJSONPrimitive(value)) {
        if (value !== current) {
            // @ts-ignore
            yMap.set(key, value);
        } else {
            if (value === undefined) {
                // yMap.get returns undefined if the key doesn't exist, as well if the key exists and the value is undefined
                if (!yMap.has(key)) {
                    // @ts-ignore
                    yMap.set(key, value);
                }
            }
        }
    } else if (current) {
        // @ts-ignore
        const update = updateYWithJSON(current, value);
        if (update) {
            // @ts-ignore
            yMap.set(key, update);
        }
    } else {
        yMap.set(key, jsonToY(value));
    }
}

function arrayToYArray<Value>(json: Value[]): Y.Array<JSONToY<Value>> {
    const yArray = new Y.Array<JSONToY<Value>>();
    yArray.push(json.map((item) => jsonToY(item)));

    return yArray;
}

export function updateYArrayWithJSON<Value>(yArray: Y.Array<JSONToY<Value>>, json: Value[]) {
    const commonLength = Math.min(yArray.length, json.length);

    for (let index = 0; index < commonLength; index++) {
        const jsonValue = json[index];
        const current = yArray.get(index);

        if (isYValue(jsonValue)) {
            if (jsonValue === current) {
                // Skip because of https://github.com/yjs/yjs/issues/551
            } else {
                yArray.delete(index, 1);
                // @ts-ignore
                yArray.insert(index, [jsonValue]);
            }
        } else if (isJSONPrimitive(jsonValue)) {
            if (jsonValue !== current) {
                yArray.delete(index, 1);
                // @ts-ignore
                yArray.insert(index, [jsonValue]);
            }
        } else {
            // @ts-ignore
            const update = updateYWithJSON(current, jsonValue);
            if (update) {
                yArray.delete(index, 1);
                // @ts-ignore
                yArray.insert(index, [update]);
            }
        }
    }

    if (yArray.length > json.length) {
        yArray.delete(json.length, yArray.length - json.length);
    } else if (yArray.length < json.length) {
        yArray.push(json.slice(yArray.length).map((item) => jsonToY(item)));
    }
}

function isJSONPrimitive(json: any): boolean {
    return (
        typeof json === 'string' ||
        typeof json === 'number' ||
        typeof json === 'boolean' ||
        json === null ||
        json === undefined
    );
}
